import { useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { AccountNumberState, InfoRequestForClient, WithdrawnInfoRequestForClient } from '@steward-app/steward-types';
import { maxUploadsForRequest } from '@steward-app/steward-types';
import { UserText } from '../../components/UserText';
import { AccountNumberCard, type NumberFlush } from './AccountNumberCard';
import { FileRequest, QuestionRequest, documentHint, type UploadsApi } from './InfoRequestsStep';
import { MAX_UPLOAD_BYTES } from './infoRequestsModel';
import { OnboardingBackToAppContext, OnboardingBrandHeader, SIGN_OUT_PRIVACY_LINE, Shell, ThankYouLeave, cls } from './OnboardingClientScreens';
import { WithdrawnRequests } from './WithdrawnRequests';
import type { Values } from './reviewAnswers';

/**
 * The client's requests page (crossmarc/steward-app#2433, `/onboarding/requests`): where the
 * request email and its reminder link, whatever state the form is in. A client who has already sent
 * the form used to land on the thank-you page and walk back into the form to find what was asked.
 *
 * ## No authorization of its own
 *
 * This is a VIEW of the onboarding form, not a second client of the API. `OnboardingFormBody`
 * renders it (the gate keeps that one component mounted across `/onboarding/form`, `/done` and
 * `/requests`), and hands it the same things the form's requests step receives: the form read's
 * requests, the same upload hook, the same answers and the same account-number cards. So it uses
 * the same sign-in, the same session and the same façade routes
 * (`/me/onboarding-forms/{firmWorkspaceId}/...`), and adds no route and no read of its own. The
 * gate in front of it is UX; the control that counts is server-side on those routes (see
 * `OnboardingClientGate.tsx`).
 *
 * ## What it shows
 *
 * Statements (a card per account), account numbers (the form's own cards), documents, then
 * questions (the mock did not draw a group for them, but a question request exists and has to be
 * answerable from the page the email links to). Each saves as it is added, exactly as in the form's
 * step; "Done for now" first waits for anything still unsaved, then says thank you.
 */

export const REQUESTS_DONE_LINE_AFTER_THANKS = 'What you added has been shared with';
export const REQUESTS_DONE_LINE_END = 'To add or update later, use the link in your email.';
/** Shown at the top of the page only when the client arrives from sending the form (steward-app#2471). */
export const REQUESTS_ARRIVED_LINE = 'Your answers are with';

const MB = MAX_UPLOAD_BYTES / (1024 * 1024);
export const STATEMENT_CARD_HINT = `A recent statement. PDF, JPG or PNG, up to ${MB} MB`;

function Group({ id, heading, children }: { id: string; heading: string; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="flex flex-col" data-testid={id}>
      <h2 id={id} className="m-0 mb-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate font-sans">{heading}</h2>
      {children}
    </section>
  );
}

export function RequestsPage({
  firmName, requests, withdrawn = [], note, api, values, onAnswer, numbers, addedCount, onDone, formIsOpen, onOpenForm, onSignOut, requestClosedNote, justSent = false,
}: {
  firmName: string | null;
  requests: readonly InfoRequestForClient[];
  withdrawn?: readonly WithdrawnInfoRequestForClient[];
  /** The latest send's note from the firm, if any. */
  note: string | undefined;
  api: UploadsApi;
  values: Values;
  onAnswer: (requestId: string, text: string, clean?: boolean) => boolean;
  numbers: {
    token: string; firmWorkspaceId: string; states: readonly AccountNumberState[] | undefined;
    onClosed: () => void; registerFlush: (requestId: string, flush: NumberFlush | null) => void;
  };
  /** How many of `requests` have something from the client (`splitRequests`). */
  addedCount: number;
  /** Waits for anything unsaved. False means something could not be saved and the page stays. */
  onDone: () => Promise<boolean>;
  /** True while the client has not sent the form: they are offered a way back into it. */
  formIsOpen: boolean;
  onOpenForm: () => void;
  onSignOut?: () => void;
  /** Said when a request vanished while the client was on the page. */
  requestClosedNote?: ReactNode;
  /** True only when the client got here by sending the form, not from the email link. */
  justSent?: boolean;
}) {
  const backToApp = useContext(OnboardingBackToAppContext);
  const firm: ReactNode = firmName ? <UserText>{firmName}</UserText> : 'Your advisor';
  const firmInSentence: ReactNode = firmName ? firm : 'your advisor';
  const [done, setDone] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [failed, setFailed] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => { headingRef.current?.focus(); }, [done]);

  // A file still going up (mint, PUT, commit) can yet fail, and a failure after "Thank you" would be
  // shown nowhere. So Done is off while any upload or removal is running; the card says "Adding...".
  const working = Object.values(api.busy).some((b) => b !== undefined);

  const statements = requests.filter((r) => r.kind === 'statement');
  const accountNumbers = requests.filter((r) => r.kind === 'account-number');
  const documents = requests.filter((r) => r.kind === 'document');
  const questions = requests.filter((r) => r.kind === 'question');

  if (done) {
    return (
      <Shell onSignOut={onSignOut} wide hideLeave>
        <div className={cls.cardWide} data-testid="requests-done">
          <OnboardingBrandHeader firmName={firmName} />
          <h1 ref={headingRef} tabIndex={-1} className={`${cls.h2} outline-none`}>Thank you.</h1>
          <p className={cls.body}>{REQUESTS_DONE_LINE_AFTER_THANKS} {firmInSentence}. {REQUESTS_DONE_LINE_END}</p>
          {!backToApp && <p className={cls.body} data-testid="requests-sign-out-line">{SIGN_OUT_PRIVACY_LINE}</p>}
          <ThankYouLeave onSignOut={onSignOut} />
          {formIsOpen && <button type="button" className={cls.quiet} onClick={onOpenForm}>Continue your form</button>}
        </div>
      </Shell>
    );
  }

  const finish = async () => {
    setFinishing(true); setFailed(false);
    let ok = false;
    try { ok = await onDone(); } catch { ok = false; }
    setFinishing(false);
    if (ok) setDone(true); else setFailed(true);
  };

  return (
    <Shell onSignOut={onSignOut} wide>
      <div className={cls.cardWide} data-testid="requests-page">
        <OnboardingBrandHeader firmName={firmName} />
        {justSent && <p id="requests-arrived" className="m-0 text-[13px] text-navy font-sans" data-testid="requests-arrived">{REQUESTS_ARRIVED_LINE} {firmInSentence}.</p>}
        {requests.length === 0 ? (
          <>
            <h1 ref={headingRef} tabIndex={-1} aria-describedby={justSent ? 'requests-arrived' : undefined} className={`${cls.h2} outline-none`}>Nothing is waiting for you.</h1>
            <p className={cls.body}>{firm} has no open requests for you right now.</p>
          </>
        ) : (
          <>
            <h1 ref={headingRef} tabIndex={-1} aria-describedby={justSent ? 'requests-arrived' : undefined} className={`${cls.h2} outline-none`}>{firm} asked for a few things.</h1>
            <p className={cls.body}>Add what you can now. Each one saves as you add it, and you can come back for the rest.</p>
          </>
        )}
        {note && (
          <div className="border-l-2 border-line pl-3" data-testid="onb-req-note">
            <p className="text-[11px] uppercase tracking-[0.08em] text-slate font-sans m-0 mb-1">A note from your advisor</p>
            <p className="text-[13px] text-navy font-sans m-0 whitespace-pre-wrap break-words"><UserText>{note}</UserText></p>
          </div>
        )}
        {requestClosedNote && <p role="status" className="m-0 text-[13px] text-navy font-sans" data-testid="onb-req-closed">{requestClosedNote}</p>}
        {statements.length > 0 && (
          <Group id="requests-statements" heading="Statements">
            {statements.map((r) => <FileRequest key={r.id} request={r} api={api} locked={finishing} title={r.accountLabel || 'A recent statement'} hint={STATEMENT_CARD_HINT} />)}
          </Group>
        )}
        {accountNumbers.length > 0 && (
          <Group id="requests-account-numbers" heading="Account numbers">
            {accountNumbers.map((r) => (
              <AccountNumberCard key={r.id} request={r} state={numbers.states?.find((s) => s.requestId === r.id)} token={numbers.token}
                firmWorkspaceId={numbers.firmWorkspaceId} firm={firm} onClosed={numbers.onClosed} registerFlush={numbers.registerFlush} />
            ))}
          </Group>
        )}
        {documents.length > 0 && (
          <Group id="requests-documents" heading="Documents">
            {documents.map((r) => <FileRequest key={r.id} request={r} api={api} locked={finishing} hint={documentHint(maxUploadsForRequest(r.kind))} />)}
          </Group>
        )}
        {questions.length > 0 && (
          <Group id="requests-questions" heading="Questions">
            {questions.map((r) => <QuestionRequest key={r.id} request={r} values={values} onAnswer={onAnswer} />)}
          </Group>
        )}
        <WithdrawnRequests firmCap={firm} withdrawn={withdrawn} testId="requests-withdrawn" />
        <p role="status" className="sr-only"><span key={api.announcement.seq}>{api.announcement.text}</span></p>
        {requests.length > 0 && <p className={`m-0 ${cls.small}`} data-testid="requests-count">{addedCount} of {requests.length} added.</p>}
        {failed && <p role="alert" className="m-0 text-[13px] text-navy font-sans">Some of your answers are not saved yet. Please try again.</p>}
        <button type="button" className={cls.primary} disabled={finishing || working} onClick={() => void finish()}>
          {finishing ? 'Saving...' : 'Done for now'}
        </button>
        {formIsOpen && (
          <button type="button" className={cls.quiet} onClick={onOpenForm}>Continue your form</button>
        )}
      </div>
    </Shell>
  );
}
